import { useEffect, useRef } from "react";
import { Animated } from "react-native";

import { useAppActive } from "./useAppActive";

// THE one "look here" cue cycle: fade up → hold → fade down, or a static
// hold when the athlete has asked for less motion.
//
// ⭐ What is shared and what is NOT. `useEffortNudge` already owned this
// cycle, wrapped in a backoff schedule (1s, then 9s doubling to 180s) that is
// specific to a pending set waiting to be logged. LG-49's history pill needs
// the same CYCLE on a completely different schedule. Extracting the whole
// hook would have forced one concept onto two — so only the cycle moved, and
// the schedule stayed where it belongs. `useEffortNudge`'s jest suite
// (`useEffortNudge.test.tsx`) is what makes that split safe to assert: the
// cadence it locks did not change.
//
// ⛔ Reduced motion is a swap, never silence. Antenore's decision on
// `UnseenCue` (2026-08-17) rules out both extremes — animating anyway, and
// going quiet — because respecting the setting must not cost the athlete the
// only signal that something is there. So the reduced path still appears and
// still clears; it just stops easing.
//
// Opacity-only, so every cue runs on the native driver.

/** Full opacity. A sub-1 peak sinks text cues under the AA floor (AP-0i). */
export const CUE_PEAK = 1;
/** How long the static (reduced-motion) cue stays up before clearing. */
const STATIC_HOLD_MS = 2_500;
const RISE_MS = 600;
const HOLD_MS = 1_100;
const FALL_MS = 700;

export type AttentionCue = {
  /** Run one cycle. Safe to call again mid-cycle; the last call wins. */
  fire: () => void;
  /** Cancel anything in flight and settle back to invisible. */
  stop: () => void;
};

export function createAttentionCue(
  opacity: Animated.Value,
  reducedMotion: boolean,
): AttentionCue {
  let staticTimer: ReturnType<typeof setTimeout> | null = null;

  const fire = () => {
    if (reducedMotion) {
      if (staticTimer) clearTimeout(staticTimer);
      opacity.setValue(CUE_PEAK);
      staticTimer = setTimeout(() => opacity.setValue(0), STATIC_HOLD_MS);
      return;
    }
    // One slow, readable cycle. A fast double-blink was tried and reverted:
    // it vanished before the thing it pointed at could be read (AP-0l).
    Animated.sequence([
      Animated.timing(opacity, {
        toValue: CUE_PEAK,
        duration: RISE_MS,
        useNativeDriver: true,
      }),
      Animated.delay(HOLD_MS),
      Animated.timing(opacity, {
        toValue: 0,
        duration: FALL_MS,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const stop = () => {
    if (staticTimer) clearTimeout(staticTimer);
    staticTimer = null;
    opacity.stopAnimation(() => opacity.setValue(0));
  };

  return { fire, stop };
}

/** Delay before the first cue — long enough that the surface has settled and
 *  the athlete's eye is not still travelling. */
const FIRST_CUE_MS = 1_200;

// A cue on a plain, rare, repeating beat — the second schedule built on the
// cycle above (the first is `useEffortNudge`'s backoff).
//
// ⛔ Deliberately NOT a backoff. The effort nudge escalates its silence
// because the thing it asks for gets more overdue by the second; this one
// states a FACT that does not change while you look at it ("you last did this
// six months ago"). A fact repeated ever more rarely is just a fact you stop
// being told. Antenore, 2026-09-01: *«intermittente, come tutte le altre, lo
// fa di rado, è accettabile»* — rare is what the beat is for, and the athlete
// has a switch (`exerciseAgePulsePreference`) if it is still too much.
//
// The caller ends it by flipping `active` — e.g. once the history is open,
// the pill has been read and has nothing left to announce.
export function useAttentionPulse(args: {
  /** True while there is something to point at. */
  active: boolean;
  /** The user's switch for this cue. */
  enabled: boolean;
  /** OS reduce-motion → static swap, never silence. */
  reducedMotion: boolean;
  /** Identity of what is being announced; a change restarts the beat. */
  cueKey: string;
  /** Gap between cues. */
  everyMs: number;
}): Animated.Value {
  const { active, enabled, reducedMotion, cueKey, everyMs } = args;
  const opacity = useRef(new Animated.Value(0)).current;
  // Nobody sees a pulse in background (#325).
  const appActive = useAppActive();

  useEffect(() => {
    if (!active || !enabled || !appActive) {
      opacity.setValue(0);
      return;
    }
    const cue = createAttentionCue(opacity, reducedMotion);
    const beat = setInterval(cue.fire, everyMs);
    const first = setTimeout(cue.fire, FIRST_CUE_MS);
    return () => {
      clearTimeout(first);
      clearInterval(beat);
      cue.stop();
    };
  }, [active, enabled, appActive, reducedMotion, cueKey, everyMs, opacity]);

  return opacity;
}
